import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import classnames from 'classnames/bind';

import styles from './HorizontalBarChart.module.css';
import LegendEntry from './LegendEntry';
import { formatNumberWithFixedPrecision } from '../utils/format-number-with-fixed-precision';

const cx = classnames.bind(styles);

const GROUP_KEY = Symbol('group_key');

export default class HorizontalBarChart extends React.Component {
  static GROUP_KEY = GROUP_KEY;
  static propTypes = {
    classes: PropTypes.shape({
      container: PropTypes.string,
      chart: PropTypes.string,
    }),
    legend: PropTypes.bool,
    range: PropTypes.arrayOf(PropTypes.number),
    yAxis: PropTypes.shape({
      format: PropTypes.func,
      textFormat: PropTypes.func,
      ticks: PropTypes.number,
    }),
    columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        [GROUP_KEY]: PropTypes.string.isRequired,
      })
    ).isRequired,
    title: PropTypes.string,
  };

  static defaultProps = {
    classes: {},
    yAxis: {
      format: null,
      ticks: 4,
    },
    range: null,
  };

  state = {
    margin: { top: 10, right: 10, bottom: 10, left: 80 },
  };

  ref = React.createRef();

  get svg() {
    return d3.select(this.ref.current);
  }

  get rect() {
    return this.ref.current.getBoundingClientRect();
  }

  componentDidMount() {
    this.draw();
  }

  componentDidUpdate() {
    this.draw();
  }

  draw() {
    const {
      svg,
      rect: { width, height },
      state: {
        margin: { ...margin },
      },
      props: { columns = [], data = [], range },
    } = this;

    svg.selectAll('g').remove();

    const y0 = d3
      .scaleBand()
      .domain(data.map((d) => d[GROUP_KEY]))
      .rangeRound([height - margin.bottom, margin.top])
      .paddingInner(0.2);

    const y1 = d3.scaleBand().domain(columns).rangeRound([y0.bandwidth(), 0]).padding(0.2);

    const x = d3
      .scaleLinear()
      .domain(range || [0, d3.max(data, (d) => d3.max(columns, (key) => d[key]))])
      .rangeRound([margin.left, width - margin.right]);

    const xAxis = (g) =>
      g
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(4, 's').tickSize(-height))
        .call((g) => {
          g.select('.domain').remove();
          g.selectAll('.tick line').attr('stroke', '#979797').attr('stroke-width', '.8');
        });

    const yAxis = (g) =>
      g
        .attr('transform', `translate(${margin.left},0)`)
        .call(d3.axisLeft(y0).ticks(null, 's'))
        .call((g) => {
          g.select('.domain').remove();
          g.selectAll('line').remove();
          g.selectAll('text').attr('class', cx('y-axis'));
        });

    svg.append('g').call(yAxis);
    svg.append('g').call(xAxis);

    const canvas = svg
      .append('g')
      .selectAll('g')
      .data(data)
      .join('g')
      .attr('transform', (d) => `translate(1,${y0(d[GROUP_KEY])})`);

    canvas
      .selectAll('rect')
      .data((d) => columns.map((key) => ({ key, group: d[GROUP_KEY], value: d[key] })))
      .join('rect')
      .attr('x', (d) => x(0))
      .attr('y', (d) => y1(d.key))
      .attr('height', y1.bandwidth())
      .attr('width', (d) => x(d.value) - x(0))
      .attr('fill', (d) => this.color(d.key, d.group, 'fill'))
      .attr('stroke', (d) => this.color(d.key, d.group, 'stroke') || null)
      .attr('stroke-width', 1);

    canvas
      .selectAll('text')
      .data((d) => columns.map((key) => ({ key, group: d[GROUP_KEY], value: d[key] })))
      .enter()
      .append('text')
      .attr('x', (d) => x(d.value) + 10)
      .attr('y', (d) => y1(d.key) - y1.bandwidth() / 2)
      .attr('dy', (d) => 10)
      .attr('text-anchor', 'middle')
      .attr('alignment-baseline', 'bottom')
      .attr('font-size', '8px')
      .attr('fill', '#767676')
      .text((d) => formatNumberWithFixedPrecision(d.value, 1));
  }

  render() {
    const { columns = [], color, classes, title, legend, rangelabel } = this.props;
    this.color = color || d3.scaleOrdinal().range(['#65CAD8', '#1668B3', '#BABABA']);
    return (
      <div className={cx('root', classes.container)}>
        {title && <div className="box">{title}</div>}
        <div className={cx('group')}>
          <svg className={classes.graph} ref={this.ref}>
            {rangelabel && (
              <>
                <text x="80" y="175" fill="#767676" className={cx('x-label')}>
                  {rangelabel[0]}
                </text>
                <text x="385" y="175" fill="#767676" className={cx('x-label')}>
                  {rangelabel[1]}
                </text>
              </>
            )}
          </svg>
          {legend !== false && (
            <div className={cx('legend')}>
              {columns.map((x) => (
                <LegendEntry key={x} color={this.color(x)}>
                  {x}
                </LegendEntry>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}
